import { Box, Stack } from '@mui/material'
import dynamic from 'next/dynamic'
import { FC } from 'react'
import { IconButton } from 'utoniq-ui'

import {
  ItemThumbnailType,
  MagazinePageDirection,
  MagazinePageType,
  ResourceFragment,
} from '@/clients/utoniq-core/schema'
import {
  ItemReversibleThumbnail,
  ItemStatusCell,
  Loading,
} from '@/components/organisms'
import { DRMMagazinePlayer } from '@/components/organisms/resourcePlayer/MagazinePlayer/DRMMagazinePlayer'
import { ThumbnailActionAreaCell } from '@/components/pages/OwnedItemDetail/template/components/ThumbnailActionArea'
import { Cell } from '@/core/architecture'
import { handleChunkLoadError } from '@/lib/errorHandler'

import { State } from './'
import { ConsoleItemDescription } from './ConsoleItemDescription'
export type ConsoleItemDetailMagazineProps = {
  loading: boolean
  item: State['item']
  isFlipped: boolean
  onReverseThumbnail: () => void
  onViewMagazine: () => void
  onOpenFullScreen: () => void
  onOpenMultipleImage: () => void
}

export const ConsoleItemDetailMagazine: Cell<ConsoleItemDetailMagazineProps> =
  ({
    loading,
    item,
    isFlipped,
    onReverseThumbnail,
    onViewMagazine,
    onOpenMultipleImage,
    onOpenFullScreen,
  }) => {
    return (
      <>
        <Box sx={{ position: 'relative' }}>
          <ThumbnailActionAreaCell
            onOpenFullScreen={onOpenFullScreen}
            onOpenMultipleImage={onOpenMultipleImage}
            numberImage={item?.images?.length ?? 0}
          />
          <ItemReversibleThumbnail
            loading={loading}
            flipProps={{
              isFlipped,
            }}
            btnPlay={{
              type: 'back',
              onClick: onViewMagazine,
            }}
            imageAreaProps={{
              thumbnail: item?.thumbnail,
              size:
                item?.thumbnailType === ItemThumbnailType.Square
                  ? 'x-large-square'
                  : 'x-large',
              onClick: onReverseThumbnail,
            }}
          />
        </Box>
        {item && !loading && (
          <Stack sx={{ pt: 3 }}>
            <ItemStatusCell
              salesStatus={item.salesStatus}
              isPublished={item.isPublished}
              isDrm={item.isDrm}
              isPremium={item.isPremium}
            />
          </Stack>
        )}
        <ConsoleItemDescription item={item} loading={loading} />

        <Box sx={{ mt: 6, mb: 2 }}>
          {isFlipped ? (
            <IconButton.Rotate
              size="small"
              onClick={onReverseThumbnail}
              bgcolor="primary.main"
              color="white"
            />
          ) : (
            <IconButton.MagazineOn
              size="small"
              onClick={onReverseThumbnail}
              bgcolor="primary.main"
              color="white"
            />
          )}
        </Box>
      </>
    )
  }

type MagazinePlayerModalProps = {
  loading: boolean
  open: boolean
  onClose: () => void
  item: State['item']
}
export const MagazinePlayerModal: FC<MagazinePlayerModalProps> = ({
  loading,
  open,
  onClose,
  item,
}) => {
  if (!open) {
    return null
  }

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
        <Loading />
      </Box>
    )
  }

  if (item?.isDrm) {
    return (
      <DRMMagazinePlayer
        open={open}
        onClose={onClose}
        resource={item?.resource as ResourceFragment}
      />
    )
  }

  if (item?.metadata?.magazine?.pageType === MagazinePageType.Image) {
    return (
      <MangaPlayer
        loading={false}
        open={open}
        onClose={onClose}
        item={item ?? undefined}
        hiddenLastPage
        isConsolePage
        direction={
          item?.metadata?.magazine?.pageDirection || MagazinePageDirection.Left
        }
      />
    )
  }

  return (
    <MagazinePlayer
      loading={false}
      open={open}
      onClose={onClose}
      resourceUrl={item?.resource?.uri || ''}
      item={item ?? undefined}
      hiddenLastPage
      isConsolePage
      direction={
        item?.metadata?.magazine?.pageDirection || MagazinePageDirection.Left
      }
    />
  )
}

const MagazinePlayer = dynamic(
  () =>
    import('@/components/organisms/resourcePlayer/MagazinePlayer')
      .then(md => md.MagazinePlayer)
      .catch(async error => {
        await handleChunkLoadError(error)
        return () => null
      }),
  { ssr: false }
)

const MangaPlayer = dynamic(
  () =>
    import('@/components/organisms/resourcePlayer/MagazinePlayer/MangaPlayer')
      .then(md => md.MangaPlayer)
      .catch(async error => {
        await handleChunkLoadError(error)
        return () => null
      }),
  { ssr: false }
)
